import React, { useState, useEffect } from 'react';
import { Button, Switch, Input, Spin } from 'antd';
import {
	SendOutlined,
	ThunderboltOutlined,
	PhoneOutlined,
	UserOutlined,
	MessageOutlined,
	ArrowLeftOutlined,
	ArrowUpOutlined,
} from '@ant-design/icons';
import { useConversationScroll } from 'src/Hooks/useConversationScrollHook';
import { useMessageActions } from 'src/Hooks/useMessageActionsHook';
import { ConversationViewProps } from '@/types/TTextingPlatform';

const { TextArea } = Input;

interface ConversationViewPropsUpdated extends Omit<ConversationViewProps, 'serverURL'> {
	selectedAgentId: string;
	setSelectedClient: (client: string) => void;
	loadMoreMessages?: (phoneNumber: string, cursor: string) => Promise<any[]>;
}

const ConversationView: React.FC<ConversationViewPropsUpdated> = ({
	selectedClient,
	messagesData,
	savedContacts = {},
	dontAutoReplyToTheseNumbers = [],
	renderPSTDate,
	username,
	provisionedNumber,
	theirNumber,
	selectedAgentId,
	setSelectedClient,
	loadMoreMessages,
}) => {
	const [localMessages, setLocalMessages] = useState(messagesData || {});
	const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);

	const { showScrollToBottom, messagesEndRef, messagesContainerRef, scrollToBottom } =
		useConversationScroll({
			selectedClient,
			localMessages,
			isLoadingMoreMessages,
			loadMoreMessages: loadMoreMessages
				? async (phoneNumber, cursor) => {
						setIsLoadingMoreMessages(true);
						try {
							return await loadMoreMessages(phoneNumber, cursor);
						} finally {
							setIsLoadingMoreMessages(false);
						}
					}
				: undefined,
		});

	const {
		msgVal,
		setMsgVal,
		handleSendMessage,
		handleToggleAIAutoReply,
		handleKeyPress,
		sendMessageMutation,
		updateAIAutoReplyMutation,
	} = useMessageActions({
		selectedClient,
		selectedAgentId,
		provisionedNumber,
		theirNumber,
		username,
		localMessages,
		setLocalMessages,
		onMessageSent: () => {
			setTimeout(() => scrollToBottom(), 100);
		},
	});

	useEffect(() => {
		if (messagesData) {
			setLocalMessages(messagesData);
		}
	}, [messagesData]);

	const getInitials = (name: string) => {
		if (!name) {
			return '?';
		}
		const words = name.split(' ').filter(Boolean);
		if (words.length === 0) {
			return '?';
		}
		if (words.length === 1) {
			return words[0].charAt(0).toUpperCase();
		}
		return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
	};

	if (!selectedClient) {
		return (
			<div className="flex flex-col items-center justify-center h-full bg-gray-50 px-4 sm:px-8">
				<div className="text-center max-w-md">
					<div className="w-20 h-20 sm:w-32 sm:h-32 mx-auto mb-6 sm:mb-8 bg-gray-200 rounded-full flex items-center justify-center">
						<MessageOutlined className="text-3xl sm:text-5xl text-gray-400" />
					</div>

					<h2 className="text-xl sm:text-2xl font-light text-gray-600 mb-3 sm:mb-4">
						SMS for Business
					</h2>

					<p className="text-sm sm:text-base text-gray-500 leading-relaxed mb-4 sm:mb-6">
						Send and receive messages with your customers. Select a conversation from the sidebar to
						get started, or create a new chat to begin messaging.
					</p>

					<div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
						<div className="flex items-center gap-2 text-green-700 justify-center sm:justify-start">
							<ThunderboltOutlined className="text-base sm:text-lg" />
							<span className="font-medium text-sm sm:text-base">AI-Powered Responses</span>
						</div>
						<p className="text-xs sm:text-sm text-green-600 mt-1">
							Enable AI auto-reply to respond to customers automatically when you're away
						</p>
					</div>
				</div>
			</div>
		);
	}

	const contactName = savedContacts?.[selectedClient] || selectedClient;
	const hasContactName = Boolean(savedContacts?.[selectedClient]);
	const isAIAutoReplyEnabled = !dontAutoReplyToTheseNumbers.includes(selectedClient);
	const messages = localMessages?.[selectedClient]?.messages || [];

	return (
		<div className="flex flex-col h-full bg-gray-50 relative">
			<div className="px-3 sm:px-6 py-3 sm:py-4 bg-white border-b border-gray-200 shadow-sm">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
						<Button
							type="text"
							shape="circle"
							icon={<ArrowLeftOutlined />}
							onClick={() => setSelectedClient('')}
							className="lg:hidden flex-shrink-0 hover:bg-gray-100"
							size="small"
						/>

						<div className="flex-shrink-0">
							<div
								className={`w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-base ${
									hasContactName
										? 'shadow-md bg-blue-500'
										: 'bg-gradient-to-br from-gray-400 to-gray-500'
								}`}
							>
								{hasContactName ? getInitials(contactName) : <UserOutlined />}
							</div>
						</div>

						<div className="flex-1 min-w-0">
							{hasContactName ? (
								<div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
									<h2 className="text-sm mt-2 sm:text-lg font-semibold text-gray-900 truncate">
										{contactName}
									</h2>
									<div className="flex items-center gap-1 text-xs sm:text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full w-fit">
										<PhoneOutlined className="text-xs" />
										<span className="font-medium truncate">{selectedClient}</span>
									</div>
								</div>
							) : (
								<div>
									<h2 className="text-sm sm:text-lg font-semibold text-gray-900 truncate">
										{selectedClient}
									</h2>
								</div>
							)}
						</div>
					</div>

					<div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
						<div className="hidden sm:flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-full">
							<ThunderboltOutlined
								className={`text-sm ${isAIAutoReplyEnabled ? 'text-amber-500' : 'text-gray-400'}`}
							/>
							<span className="text-sm font-medium text-gray-700">AI Reply</span>
							<Switch
								checked={isAIAutoReplyEnabled}
								onChange={handleToggleAIAutoReply}
								loading={updateAIAutoReplyMutation.isPending}
								size="small"
								className={isAIAutoReplyEnabled ? 'bg-blue-500' : ''}
							/>
						</div>

						<div className="sm:hidden flex items-center gap-2">
							<ThunderboltOutlined
								className={`text-sm ${isAIAutoReplyEnabled ? 'text-amber-500' : 'text-gray-400'}`}
							/>
							<Switch
								checked={isAIAutoReplyEnabled}
								onChange={handleToggleAIAutoReply}
								loading={updateAIAutoReplyMutation.isPending}
								size="small"
								className={isAIAutoReplyEnabled ? 'bg-blue-500' : ''}
							/>
						</div>
					</div>
				</div>
			</div>

			<div
				ref={messagesContainerRef}
				className="flex-1 overflow-y-auto px-3 sm:px-6 py-2 sm:py-4 space-y-2 sm:space-y-3"
			>
				{isLoadingMoreMessages && (
					<div className="flex justify-center items-center py-2">
						<Spin size="small" />
						<span className="ml-2 text-sm text-gray-500">Loading older messages...</span>
					</div>
				)}

				{messages.length === 0 ? (
					<div className="flex flex-col items-center justify-center h-full text-center px-4">
						<div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 rounded-full flex items-center justify-center mb-3 sm:mb-4">
							<SendOutlined className="text-2xl sm:text-3xl text-gray-400" />
						</div>
						<h3 className="text-base sm:text-lg font-medium text-gray-600 mb-2">
							Start the conversation
						</h3>
						<p className="text-xs sm:text-sm text-gray-500 max-w-sm">
							Send a message to begin your conversation with {contactName}
						</p>
					</div>
				) : (
					messages.map((message, idx) => {
						if (!message) {
							return null;
						}

						const isFromClient = message.from === selectedClient;

						return (
							<div
								key={`message-${idx}-${message.timestamp}`}
								className={`flex ${isFromClient ? 'justify-start' : 'justify-end'}`}
							>
								<div className="max-w-xs sm:max-w-sm lg:max-w-md">
									<div
										className={`px-3 sm:px-4 py-2 sm:py-3 rounded-2xl shadow-sm ${
											isFromClient
												? 'bg-white text-gray-900 rounded-bl-md'
												: 'text-white rounded-br-md bg-blue-500'
										}`}
									>
										<p className="text-xs sm:text-sm whitespace-pre-wrap break-words leading-relaxed">
											{message.message || ''}
										</p>
									</div>

									<div className={`mt-1 px-2 ${isFromClient ? 'text-left' : 'text-right'}`}>
										<span className="text-xs text-gray-500">
											{message.timestamp && renderPSTDate ? renderPSTDate(message.timestamp) : ''}
										</span>
									</div>
								</div>
							</div>
						);
					})
				)}
				<div ref={messagesEndRef} />
			</div>

			{showScrollToBottom && (
				<div className="absolute bottom-20 right-4 z-10">
					<Button
						type="primary"
						shape="circle"
						icon={<ArrowUpOutlined className="rotate-180" />}
						onClick={scrollToBottom}
						className="shadow-lg bg-blue-500 hover:bg-blue-600 border-0"
						size="large"
					/>
				</div>
			)}

			<div className="px-3 sm:px-6 py-3 sm:py-4 bg-white border-t border-gray-200">
				<div className="pr-14">
					<div className="flex border border-gray-300 rounded-xl sm:rounded-2xl overflow-hidden focus-within:border-blue-400 focus-within:shadow-sm">
						<TextArea
							value={msgVal}
							onChange={(e) => setMsgVal(e.target.value)}
							onPressEnter={handleKeyPress}
							placeholder="Type a message..."
							autoSize={{ minRows: 1, maxRows: 4 }}
							className="flex-1 resize-none border-0 focus:outline-none focus:ring-0 focus:border-0 px-3 sm:px-4 py-2 sm:py-3 bg-transparent text-sm sm:text-base"
							disabled={sendMessageMutation.isPending}
						/>
						<Button
							type="primary"
							onClick={handleSendMessage}
							disabled={!msgVal?.trim()}
							loading={sendMessageMutation.isPending}
							className="bg-blue-500 hover:bg-blue-600 border-0 rounded-none px-4 sm:px-6 text-xs sm:text-sm font-medium h-auto flex items-center justify-center"
						>
							Send
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ConversationView;
